import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { mediaSrc } from "../lib/media";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import DemoBadge from "../components/DemoBadge";
import { MediaTile, EventCard, PersonCard, EmptyState, Loader } from "../components/Blocks";

export default function PartnerDetail() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/partners/${slug}`).then(({ data }) => setPartner(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <EmptyState message="Partenaire introuvable." testid="partner-not-found" />;
  if (!partner) return <Loader />;

  return (
    <div data-testid="partner-detail-page">
      <Seo
        title={partner.business_name}
        description={(partner.atmosphere_description || partner.short_description || "").slice(0, 180)}
        path={`/partenaires/${partner.slug}`}
        image={partner.hero_media_url || (partner.gallery_media || [])[0]?.url}
        kind="partner"
        type="article"
      />
      <section className={`relative flex items-end overflow-hidden ${partner.hero_media_url ? "min-h-[65vh]" : "min-h-[38vh] bg-gradient-to-br from-[#1a0f14] via-[#0a0a0a] to-[#050505]"}`}>
        {partner.hero_media_url && (
          <img src={mediaSrc(partner.hero_media_url)} alt={partner.business_name} className="absolute inset-0 w-full h-full object-cover opacity-75" />
        )}
        <div className="absolute inset-0 vb-scrim" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-16 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="vb-label">{partner.category}</span>
            <DemoBadge show={partner.is_demo} />
          </div>
          <h1 className="vb-display text-4xl sm:text-5xl lg:text-7xl font-black" data-testid="partner-title">
            {partner.business_name}
          </h1>
          <p className="mt-4 text-sm text-neutral-300">
            {partner.city}
            {partner.location ? ` · ${partner.location}` : ""}
          </p>
          <div className="mt-6">
            <ShareButton
              path={`/partenaires/${partner.slug}`}
              title={`${partner.business_name} — VytaliBoots`}
              text={`Découvrez l'ambiance de ${partner.business_name} sur VytaliBoots.`}
              testid="partner-share-button"
            />
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-14">
          <div>
            <div className="vb-label mb-4">L'atmosphère</div>
            <p className="vb-display text-2xl lg:text-3xl leading-snug max-w-3xl" data-testid="partner-atmosphere">
              {partner.atmosphere_description}
            </p>
            <p className="mt-6 text-base text-neutral-400 max-w-3xl">{partner.short_description}</p>
          </div>

          <div>
            <div className="vb-label mb-6">Galerie</div>
            {(partner.gallery_media || []).length === 0 && (partner.media || []).length === 0 && (partner.gallery || []).length === 0 ? (
              <EmptyState message="Aucun média publié." testid="partner-media-empty" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4" data-testid="partner-media-grid">
                {(partner.gallery_media || []).map((m, i) => (
                  <MediaTile key={`c-${m.id}`} item={m} testid={`partner-gallery-media-${i}`} className="aspect-square" />
                ))}
                {(partner.media || [])
                  .filter((m) => !(partner.gallery_media || []).some((g) => g.id === m.id))
                  .map((m, i) => (
                    <MediaTile key={m.id} item={m} testid={`partner-media-${i}`} className="aspect-square" />
                  ))}
                {(partner.gallery || []).map((url, i) => (
                  <MediaTile
                    key={`g-${i}`}
                    item={{ url, type: "photo", is_demo: partner.is_demo }}
                    testid={`partner-gallery-${i}`}
                    className="aspect-square"
                  />
                ))}
              </div>
            )}
          </div>

          {(partner.events || []).length > 0 && (
            <div>
              <div className="vb-label mb-6">Événements associés</div>
              <div className="grid md:grid-cols-2 gap-6" data-testid="partner-events-grid">
                {partner.events.map((e, i) => (
                  <EventCard key={e.id} event={e} testid={`partner-event-${i}`} />
                ))}
              </div>
            </div>
          )}

          {(partner.creators || []).length > 0 && (
            <div>
              <div className="vb-label mb-6">Créateurs associés</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="partner-creators-grid">
                {partner.creators.map((p, i) => (
                  <PersonCard key={p.id} person={p} testid={`partner-creator-${i}`} />
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-8">
          <div className="vb-card p-6">
            <div className="vb-label mb-3">Informations</div>
            <div className="text-sm text-neutral-300 space-y-2">
              <div>{partner.category}</div>
              <div>{partner.city}</div>
              {partner.location && <div className="text-neutral-500">{partner.location}</div>}
            </div>
            {partner.cta_url && (
              <a
                href={partner.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partner-cta"
                className="vb-btn vb-btn-primary mt-6 w-full"
              >
                {partner.cta_label || "En savoir plus"} <ExternalLink size={14} />
              </a>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
